import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useRouter } from 'next/router';
import type { MarketingPreferencesResponse, PaymentMethod, SiteSwitchResponse, Store } from '../codegen/types';
import type { MarketingCategory } from '../types/marketingPreferences';
import { Locale, countryNameMapperROE, getCountryKey } from '../utils/internationalization';
import { useLocaleContext } from './localeContext';

type MarketingPreferences = {
  marketingPreferenceDefaultEnabled: boolean | null;
  marketingPreferences: MarketingCategory;
  languages: string[];
};

type StaticContextType = {
  host: string | '';
  isServerDesktop?: boolean;
  previousURL: string | '';
  paymentMethods: PaymentMethod[] | null;
  marketingPreferences: MarketingPreferences | null;
  storeData: Store | null;
  switchSitesData: SiteSwitchResponse[] | null;
  baseUrl: string | '';
};

const StaticContext = createContext<StaticContextType>({
  host: '',
  previousURL: '',
  paymentMethods: null,
  marketingPreferences: null,
  storeData: null,
  switchSitesData: null,
  baseUrl: '',
});

export const useStaticContext = () => useContext(StaticContext);

type StaticContextProviderProps = {
  children: ReactNode;
  host: string | '';
  isServerDesktop?: boolean;
  paymentMethods: PaymentMethod[] | null;
  marketingPreferences: MarketingPreferencesResponse | null;
  storeData: Store | null;
  switchSitesData: SiteSwitchResponse[] | null;
  baseUrl: string | '';
};

export function StaticContextProvider({
  children,
  isServerDesktop,
  host,
  baseUrl,
  paymentMethods,
  marketingPreferences,
  storeData,
  switchSitesData,
}: StaticContextProviderProps) {
  const router = useRouter();
  const { locale } = useLocaleContext();
  const ref = useRef('');

  const transformedMarketingPreferences = marketingPreferences?.marketingPreferences.reduce(
    (acc, item, idx) => ({
      ...acc,
      [idx]: {
        key: item.key,
        label: item.label,
        state: !!marketingPreferences.marketingPreferenceDefaultEnabled,
      },
    }),
    {},
  ) as MarketingCategory;

  const marketingPreferencesData = useMemo(
    () => ({
      marketingPreferenceDefaultEnabled: !!marketingPreferences?.marketingPreferenceDefaultEnabled,
      marketingPreferences: transformedMarketingPreferences,
      languages: marketingPreferences?.languages || [],
    }),
    [
      marketingPreferences?.languages,
      marketingPreferences?.marketingPreferenceDefaultEnabled,
      transformedMarketingPreferences,
    ],
  );

  useEffect(() => {
    ref.current = router.asPath ?? '';
  }, [router.asPath]);

  const previousURL = `https://${host}/${locale}${ref.current}`;

  const switchSitesCountriesROE = Object.keys(countryNameMapperROE).map(
    (key) =>
      ({
        countryName: [
          {
            locale: Locale.DEFAULT_LOCALE,
            value: countryNameMapperROE[key],
          },
        ],
        currency: 'EUR',
        currencySymbol: '€',
        key: `roe-${key.toLowerCase()}`,
        languages: [`en-${key.toUpperCase()}`],
      } as SiteSwitchResponse),
  );

  const switchSitesEnLanguageData =
    switchSitesData?.map((obj) => {
      if (!obj.languages.some((lang) => lang.startsWith('en'))) {
        return { ...obj, languages: [...obj.languages, `en-${getCountryKey(obj.key).toUpperCase()}`] };
      }
      return obj;
    }) || [];

  const switchSitesLanguagesData = switchSitesEnLanguageData.map((obj) => ({
    ...obj,
    languages: obj.languages.map((lang) => {
      if (!lang.includes('-')) {
        return `${lang}-${getCountryKey(obj.key).toUpperCase()}`;
      }
      return lang;
    }),
  }));

  const combinedSwitchSites = [...switchSitesLanguagesData, ...switchSitesCountriesROE];

  const switchSitesFullData = combinedSwitchSites
    .reduce((acc: SiteSwitchResponse[], current) => {
      const isDuplicate = acc.some((item) => item.key === current.key);
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, [])
    .filter((data) => data.key !== 'roe' && data.key !== 'roe-scand');

  const staticContext = useMemo(
    () => ({
      host,
      isServerDesktop: isServerDesktop ?? undefined,
      baseUrl,
      previousURL,
      paymentMethods,
      marketingPreferences: marketingPreferencesData,
      storeData,
      switchSitesData: switchSitesFullData,
    }),
    [
      host,
      isServerDesktop,
      baseUrl,
      previousURL,
      paymentMethods,
      marketingPreferencesData,
      storeData,
      switchSitesFullData,
    ],
  );

  return <StaticContext.Provider value={staticContext}>{children}</StaticContext.Provider>;
}
